import React, { useMemo } from 'react';
import { Loader2 } from 'lucide-react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';

/**
 * Stable key for one transfer-inventory row (lot + warehouse location).
 *
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {string | null}
 */
export function getFCTransferInventoryRowKey(row) {
    if (row == null) return null;
    const { lotId, warehouseLocationId } = row;
    if (lotId === undefined || lotId === null || warehouseLocationId === undefined || warehouseLocationId === null) {
        return null;
    }
    return `${lotId}:${warehouseLocationId}`;
}

/**
 * @param {Record<string, unknown>} row
 * @returns {string}
 */
function formatLotOptionLabel(row) {
    const { itemName, locationName, lotId } = row;
    return `Lot ${lotId} — ${locationName} — ${itemName}`;
}

/**
 * Lot picker for available inventory rows (tech spec §6.5). Parent owns selected row state.
 *
 * @param {Object} props
 * @param {Array<Record<string, unknown>>} [props.rows=[]] - `transferInventory` rows from available-lots API
 * @param {Record<string, unknown> | null} [props.selectedRow]
 * @param {(row: Record<string, unknown> | null) => void} [props.onSelectedRowChange]
 * @param {boolean} [props.isLoading]
 * @param {string} [props.emptyHelperText] - Shown when there are no rows (not loading)
 */
export function FCTransferLotPickerRow({
    emptyHelperText = 'Choose a source facility and ensure inventory exists for this facility.',
    isLoading = false,
    onSelectedRowChange,
    rows = [],
    selectedRow = null,
}) {
    const rowList = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

    const options = useMemo(
        () =>
            rowList
                .map((row) => {
                    const key = getFCTransferInventoryRowKey(row);
                    if (!key) return null;
                    return { key, row, label: formatLotOptionLabel(row) };
                })
                .filter(Boolean),
        [rowList],
    );

    const keyByRow = useMemo(() => getFCTransferInventoryRowKey(selectedRow), [selectedRow]);
    const validKeys = useMemo(() => new Set(options.map((o) => o.key)), [options]);
    const selectValue = keyByRow && validKeys.has(keyByRow) ? keyByRow : '';

    const handleValueChange = (value) => {
        if (!onSelectedRowChange) return;
        if (!value) {
            onSelectedRowChange(null);
            return;
        }
        const match = options.find((o) => o.key === value);
        onSelectedRowChange(match?.row ?? null);
    };

    if (isLoading) {
        return (
            <div className="flex min-h-10 items-center gap-2 rounded-md border border-input bg-card px-3 text-muted-foreground">
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
                <span className="text-sm">Loading available lots…</span>
            </div>
        );
    }

    return (
        <div className="min-w-0">
            <span className="mb-2 block text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Available lots
            </span>
            {options.length === 0 ? (
                <p className="rounded-md border border-dashed border-muted-foreground/40 bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
                    {emptyHelperText}
                </p>
            ) : (
                <Select value={selectValue} onValueChange={handleValueChange}>
                    <SelectTrigger className="w-full justify-between" aria-label="Select lot and location">
                        <SelectValue placeholder="Select a lot (by location)" />
                    </SelectTrigger>
                    <SelectContent>
                        {options.map(({ key, label }) => (
                            <SelectItem key={key} value={key}>
                                {label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}
        </div>
    );
}

FCTransferLotPickerRow.displayName = 'FCTransferLotPickerRow';
